import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Spinner from 'react-bootstrap/Spinner';

import { URLS } from '../../resources/constants';
import { useForecastApi } from '../../resources/hooks/api/useForecastApiHook';
import KeyRatios from './KeyRatios';
import ForecastLayout from './ForecastLayout';
import { MONTH_FULL_NAMES } from './forecastUtils';

type View = 'five_year_view' | 'monthly_view';

interface Series { monthly: number[]; yearly: number[]; total: number }

/** $1,234.56 / ($1,234.56) — two decimals, losses in parentheses. */
export const money = (v: number | undefined | null): string => {
    const n = Number(v || 0);
    const s = `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return n < 0 ? `(${s})` : s;
};

/**
 * Five-Year View (Year 1-5 + total) and Monthly View (12 months of a selected
 * year + year total) of the calculated forecast: income statement down to net
 * income, then cash flow and cash on hand.
 */
const ForecastResults: React.FC<{ view: View }> = ({ view }) => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const navigate = useNavigate();
    const { forecastId } = useParams();
    const id = Number(forecastId);
    const { getResults } = useForecastApi();

    const [results, setResults] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [year, setYear] = useState<number>(1);
    const [showDetail, setShowDetail] = useState<boolean>(true);

    const load = () => {
        setLoading(true);
        getResults(id, (_m: string, resp: any) => {
            setResults(resp?.data?.data);
            setLoading(false);
        }, (msg: string) => {
            toast.error(msg);
            setLoading(false);
        });
    };
    useEffect(() => { if (id) load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const linesBy = useMemo(() => {
        const map: Record<string, any[]> = {};
        (results?.lines || []).forEach((l: any) => { (map[l.category] = map[l.category] || []).push(l); });
        return map;
    }, [results]);

    const isFive = view === 'five_year_view';
    const monthSlice = (results?.months || []).slice((year - 1) * 12, year * 12);

    /** Calendar year a forecast year starts in, e.g. 2027 for a Year 1 starting Oct 2027. */
    const yearLabel = (n: number): string => {
        const fy = results?.forecast;
        return fy ? String(Number(fy.start_year) + Math.floor((Number(fy.start_month) - 1 + (n - 1) * 12) / 12))
                  : String(t('common.year', 'Year {{n}}', { n }));
    };

    const headers = isFive
        ? [1, 2, 3, 4, 5].map(yearLabel).concat(t('results.totalCol', 'Total'))
        : monthSlice.map((m: any) => String(m.label).split(' ')[0])
            .concat(t('results.totalCol', 'Total'));

    /** The visible columns of a series for the current view. */
    const cols = (s: Series | undefined, scale = 1): number[] => {
        if (!s) return headers.map(() => 0);
        const v = isFive ? [...s.yearly, s.total] : [...s.monthly.slice((year - 1) * 12, year * 12), s.yearly[year - 1]];
        return v.map((x) => x * scale);
    };

    const S = results?.summary || {};
    const C = results?.cash || {};
    const A = results?.cash_flow?.activities;
    const label = (key: string, fallback: string) => String(t(key, fallback));

    const row = (key: string, name: string, values: number[], cls = '') => (
        <tr key={key} className={cls}>
            <td>{name}</td>
            {values.map((v, i) => <td key={i} className={`num${v < 0 ? ' neg' : ''}`}>{money(v)}</td>)}
        </tr>
    );
    const section = (key: string, name: string) => (
        <tr key={`s-${key}`} className="fyf-section"><td colSpan={headers.length + 1}>{name}</td></tr>
    );
    const details = (category: string) => (showDetail ? (linesBy[category] || []).map((l: any) =>
        row(`l-${l.id}`, `${l.account_number ? `${l.account_number} — ` : ''}${l.name}${l.description ? ` / ${l.description}` : ''}`,
            cols(l, category === 'gain_loss' ? l.sign : 1), 'fyf-detail')) : null);

    const block = (category: string, title: string, totalKey: string) => [
        section(category, title),
        ...(details(category) || []),
        row(`t-${category}`, String(t('results.total', 'Total {{name}}', { name: title })), cols(S[totalKey])),
    ];

    if (loading || !results) {
        return (
            <ForecastLayout active={view} forecastId={id}>
                <div className="fyf-empty"><Spinner animation="border" /></div>
            </ForecastLayout>
        );
    }

    const f = results.forecast;
    const kpis: [string, string, number][] = [
        ['gross_revenue', label('results.revenue', 'Revenue'), isFive ? S.gross_revenue.total : S.gross_revenue.yearly[year - 1]],
        ['gross_profit', label('results.grossProfit', 'Gross Profit'), isFive ? S.gross_profit.total : S.gross_profit.yearly[year - 1]],
        ['ebitda', 'EBITDA', isFive ? S.ebitda.total : S.ebitda.yearly[year - 1]],
        ['net_income', label('results.netIncome', 'Net Income'), isFive ? S.net_income.total : S.net_income.yearly[year - 1]],
        ['cash', label('results.cashOnHand', 'Cash on Hand'), isFive ? C.ending.total : C.ending.yearly[year - 1]],
    ];

    return (
        <ForecastLayout active={view} forecastId={id}>
            <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <button type="button" className="fyf-back" onClick={() => navigate(URLS.FIVE_YEAR_FORECAST)}>
                        ← {t('entry.back', 'All forecasts')}
                    </button>
                    <h1>{isFive ? t('nav.five_year_view', 'Five-Year View') : t('nav.monthly_view', 'Monthly View')}</h1>
                    <div className="fyf-sub mb-1">
                        {isFive
                            ? t('results.fiveYearSubtitle', 'Five-year income statement and cash on hand')
                            : t('results.monthlySubtitle', 'Monthly income statement and cash on hand for the selected year')}
                    </div>
                    <div className="fyf-meta">
                        <strong className="text-dark text-break">{f.name}</strong>
                        {' · '}{t('entry.starts', 'Starts {{month}} {{year}}', { month: MONTH_FULL_NAMES[f.start_month - 1], year: f.start_year })}
                        {' · '}{f.year_type === 'fiscal' ? t('setup.fiscal', 'Fiscal year') : t('setup.calendar', 'Calendar year')}
                    </div>
                </div>
                <div className="fyf-actions mt-0 flex-shrink-0 justify-content-end">
                    <button type="button" className="fyf-secondary" onClick={() => setShowDetail((v) => !v)}>
                        {showDetail ? t('results.hideDetail', 'Hide account detail') : t('results.showDetail', 'Show account detail')}
                    </button>
                    <button type="button" className="fyf-secondary" onClick={load}>{t('results.refresh', 'Recalculate')}</button>
                    <button type="button" className="fyf-primary"
                        onClick={() => navigate(URLS.FIVE_YEAR_FORECAST_ENTRY.replace(':forecastId', String(id)))}>
                        {t('results.editEntries', 'Edit Entries')}
                    </button>
                </div>
            </div>

            {!isFive && (
                <div className="fyf-yearsel">
                    {[1, 2, 3, 4, 5].map((y) => (
                        <button key={y} type="button" className={y === year ? 'on' : ''} onClick={() => setYear(y)}>
                            {yearLabel(y)}
                        </button>
                    ))}
                </div>
            )}

            <div className="fyf-kpis mt-3">
                {kpis.map(([k, name, v]) => (
                    <div className="fyf-kpi" key={k}>
                        <div className="k">{name}{isFive ? '' : ` · ${yearLabel(year)}`}</div>
                        <div className="v" title={money(v)}>{money(v)}</div>
                    </div>
                ))}
            </div>

            {(results.warnings || []).length > 0 && (
                <div className="fyf-note mb-3">{results.warnings.join(' ')}</div>
            )}

            {(results.lines || []).length === 0 ? (
                <div className="fyf-panel fyf-empty">{t('results.noLines', 'No forecast entries yet. Add accounts on the Forecast Entry screen.')}</div>
            ) : (
                <div className="fyf-panel">
                    <h3>{t('results.incomeStatement', 'Income Statement')}</h3>
                    <div className="fyf-table-wrap fyf-results">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('results.line', 'Forecast')}</th>
                                    {headers.map((h: string) => <th key={h} className="num">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {block('income', label('category.income', 'Gross Operating Income'), 'gross_revenue')}
                                {block('income_adjustment', label('category.income_adjustment', 'Income Adjustments'), 'income_adjustments')}
                                {row('net_revenue', label('results.netRevenue', 'Net Revenue'), cols(S.net_revenue), 'fyf-total')}
                                {block('cogs', label('category.cogs', 'Cost of Goods Sold'), 'cogs')}
                                {row('gross_profit', label('results.grossProfit', 'Gross Profit'), cols(S.gross_profit), 'fyf-total')}
                                {block('payroll', label('category.payroll', 'Payroll'), 'payroll')}
                                {block('operating_expense', label('category.operating_expense', 'Operating Expenses'), 'operating_expenses')}
                                {row('ebitda', 'EBITDA', cols(S.ebitda), 'fyf-total')}
                                {block('interest', label('category.interest', 'Interest'), 'interest')}
                                {block('depreciation', label('category.depreciation', 'Depreciation'), 'depreciation')}
                                {block('amortization', label('category.amortization', 'Amortization'), 'amortization')}
                                {block('other_income', label('category.other_income', 'Other Income'), 'other_income')}
                                {block('gain_loss', label('category.gain_loss', 'Gain / (Loss) on Sale of Assets'), 'gain_loss')}
                                {row('ibt', label('results.incomeBeforeTaxes', 'Income Before Taxes'), cols(S.income_before_taxes), 'fyf-total')}
                                {block('taxes', label('category.taxes', 'Taxes Paid'), 'taxes')}
                                {row('net_income', label('results.netIncome', 'Net Income'), cols(S.net_income), 'fyf-total')}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {(results.lines || []).length > 0 && <KeyRatios ratios={results.ratios} />}

            <div className="fyf-panel">
                <h3>{t('results.cashFlow', 'Cash Flow')}</h3>
                <div className="fyf-table-wrap fyf-results">
                    <table>
                        <thead>
                            <tr>
                                <th>{t('results.cashLine', 'Cash')}</th>
                                {headers.map((h: string) => <th key={h} className="num">{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {row('c-open', label('results.beginningCash', 'Beginning Cash'), cols(C.opening))}
                            {A ? [
                                row('c-op', label('cash.netOperating', 'Net Operating Cash (receipts - operating payments)'), cols(A.operating)),
                                row('c-inv', label('cash.netInvesting', 'Net Investing Cash (asset purchases / sales)'), cols(A.investing)),
                                row('c-fund', label('cash.netFunding', 'Net Funding Cash (loans, equity, repayments)'), cols(A.funding)),
                                row('c-div', label('cash.items.dividends', 'Dividend Payment'), cols(A.dividends, -1)),
                            ] : [
                                row('c-ni', label('results.netIncome', 'Net Income'), cols(C.net_income)),
                                row('c-add', label('results.nonCash', 'Non-cash items: D&A added back, gain/(loss) on sale removed'), cols(C.non_cash_addback)),
                                row('c-in', label('results.financingIn', 'Debt Proceeds & Equity'), cols(C.inflows)),
                                row('c-out', label('results.financingOut', 'Capex, Debt Principal & Distributions'), cols(C.outflows, -1)),
                            ]}
                            {row('c-net', label('results.netCashFlow', 'Net Cash Flow'), cols(C.net_cash_flow), 'fyf-total')}
                            {row('c-end', label('results.cashOnHand', 'Cash on Hand'), cols(C.ending), 'fyf-total')}
                        </tbody>
                    </table>
                </div>
                <div className="fyf-note">
                    {t('results.cashRule', 'Cash on Hand is a balance: each year shows its ending balance rather than a sum of months. Cash is assumed to be received and paid in the month it is earned or incurred.')}
                </div>
            </div>
        </ForecastLayout>
    );
};

export default ForecastResults;
